import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ArrowRight, MessageSquare, Sparkles, Zap, Brain } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

interface TwinProfile {
  id: number;
  name: string;
  title: string;
  tagline: string;
  brief_bio: string;
}

// Auto-scrolling card row component
const ScrollingRow: React.FC<{
  twins: TwinProfile[];
  direction: 'left' | 'right';
  speed?: number;
}> = ({ twins, direction, speed = 30 }) => {
  if (twins.length === 0) return null;
  
  // Duplicate twins for seamless loop
  const duplicatedTwins = [...twins, ...twins, ...twins];
  
  return (
    <div className="relative overflow-hidden py-2">
      <div 
        className={`flex gap-4 ${direction === 'left' ? 'animate-scroll-left' : 'animate-scroll-right'}`}
        style={{ 
          animationDuration: `${speed}s`,
          width: 'max-content'
        }}
      >
        {duplicatedTwins.map((twin, idx) => (
          <div
            key={`${twin.id}-${idx}`}
            className="flex-shrink-0 w-[320px] bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-3">
              {/* Avatar */}
              <div className="w-11 h-11 rounded-full bg-[#07E5D1] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {twin.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm">{twin.name}</h3>
                <p className="text-xs text-gray-500">{twin.title}</p>
              </div>
            </div>
            
            {twin.tagline && (
              <p className="text-xs text-[#07E5D1] mt-3 italic">
                "{twin.tagline}"
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Feature card component
const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:border-[#07E5D1] transition-colors">
    <div className="w-12 h-12 rounded-xl bg-[#E8FCFA] flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
  </div>
);

const AskCollectiveLanding: React.FC = () => {
  const navigate = useNavigate();
  const [twins, setTwins] = useState<TwinProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchTwins = async () => {
      try {
        const response = await fetch(`${API_URL}/digital-twins`);
        if (response.ok) {
          const data = await response.json();
          setTwins(data.twins || []);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error fetching twins:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchTwins();
  }, []);

  const expertCount = twins.length > 0 ? twins.length : 280;
  
  // Split twins into rows for scrolling
  const row1 = twins.slice(0, Math.ceil(twins.length / 3));
  const row2 = twins.slice(Math.ceil(twins.length / 3), Math.ceil(twins.length * 2 / 3));
  const row3 = twins.slice(Math.ceil(twins.length * 2 / 3));

  return (
    <div className="min-h-screen bg-[#FEFEFE] pb-24">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-[#07E5D1] flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-gray-900 text-lg">Ask Collective</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E8FCFA] rounded-full">
            <Sparkles className="w-4 h-4 text-[#07E5D1]" />
            <span className="text-sm font-medium text-[#07E5D1]">AI-Powered Expert Network</span>
          </div>

          {/* BluePill Logo */}
          <div className="flex justify-center">
            <a
              href="/"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={`${PUBLIC_URL}/logo.png`}
                alt="BluePill AI"
                className="h-64"
              />
            </a>
          </div>

          {/* Main headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1C1C1E] mb-6 leading-tight">
            Chat with{' '}
            <span className="text-[#07E5D1]">{expertCount}+</span>
            {' '}Industry Experts
          </h1>
          
          {/* Subheadline */}
          <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Get synthesized insights from digital twins of world-class founders, operators, and thought leaders. 
            Ask anything—our AI finds the right experts and delivers unified answers.
          </p>
        </div>
      </section>

      {/* Scrolling Cards Section */}
      <section className="py-8 bg-[#FAFBFC] overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-5 h-5 rounded-full border-2 border-[#07E5D1] border-t-transparent animate-spin" />
              <span className="text-sm">Loading experts...</span>
            </div>
          </div>
        ) : twins.length > 0 ? (
          <div className="space-y-4">
            <ScrollingRow twins={row1} direction="left" speed={240} />
            <ScrollingRow twins={row2} direction="right" speed={260} />
            <ScrollingRow twins={row3} direction="left" speed={280} />
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-gray-400">280+ experts available to help you</p>
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-400">No experts loaded</p>
          </div>
        )}
      </section>

      {/* Features Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1C1C1E] mb-4">How It Works</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Our AI analyzes your question, selects the most relevant experts, and synthesizes their perspectives into a unified answer.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Brain className="w-6 h-6 text-[#07E5D1]" />}
              title="Smart Expert Selection"
              description="Our AI automatically identifies which experts have the most relevant experience for your specific question."
            />
            <FeatureCard
              icon={<Users className="w-6 h-6 text-[#07E5D1]" />}
              title="Multiple Perspectives"
              description="Get insights from multiple experts at once—product managers, founders, growth experts, and more."
            />
            <FeatureCard
              icon={<Zap className="w-6 h-6 text-[#07E5D1]" />}
              title="Synthesized Answers"
              description="Receive a unified, coherent response that combines the best insights from all selected experts."
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 border-t border-gray-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <p className="text-sm text-gray-400">
            &copy; {new Date().getFullYear()} Bluepill Inc.
          </p>
          <a
            href="/"
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <img
              src={`${PUBLIC_URL}/logo.png`}
              alt="BluePill Logo"
              className="h-24"
            />
          </a>
        </div>
      </footer>

      {/* Fixed Bottom CTA Button */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-gray-900">Ready to get expert insights?</p>
            <p className="text-xs text-gray-500">Free to use • No signup required</p>
          </div>
          <button
            onClick={() => navigate('/ask-collective/chat')}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl font-semibold transition-colors shadow-lg shadow-[#07E5D1]/20"
          >
            <MessageSquare className="w-5 h-5" />
            <span>Chat Now</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* CSS for scrolling animation */}
      <style>{`
        @keyframes scroll-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.33%);
          }
        }
        
        @keyframes scroll-right {
          0% {
            transform: translateX(-33.33%);
          }
          100% {
            transform: translateX(0);
          }
        }
        
        .animate-scroll-left {
          animation: scroll-left linear infinite;
        }
        
        .animate-scroll-right {
          animation: scroll-right linear infinite;
        }
      `}</style>
    </div>
  );
};

export default AskCollectiveLanding;
